import React, { ComponentProps, ReactElement } from 'react'
import ReactMarkdown from 'react-markdown'
import { ThemeUIStyleObject, useThemeUI } from 'theme-ui'

export default function StyledMarkdown({
  children,
  sxx,
  ...rest
}: {
  children?: Queries.Maybe<string>
  sxx?: ThemeUIStyleObject
} & ComponentProps<typeof ReactMarkdown>): ReactElement {
  const { theme } = useThemeUI()
  return (
    <ReactMarkdown
      sx={{
        width: '100%',
        lineHeight: '2.7rem',
        h1: theme.text?.h1Big,
        h2: theme.text?.h2Small,
        h3: theme.text?.h3Big,
        p: theme.text?.paragraphSmallRegular,
        a: {
          wordBreak: 'break-word',
          textDecoration: 'none',
          color: 'neonGreen',
          ':hover': {
            color: 'neonGreen50',
          },
          ':visited': {
            color: 'neonGreen80',
          },
        },
        '&>:first-child': {
          marginTop: 0,
        },
        '&>:last-child': {
          marginBottom: 0,
        },
        ...sxx,
      }}
      components={{
        strong: ({ children }) => <strong sx={{ color: 'white' }}>{children}</strong>,
        ol: ({ children }) => (
          <ol
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2.4rem',
              li: {
                ...theme.text?.paragraphSmallRegular,
                '&::marker': {
                  fontWeight: 'bold',
                  color: 'white',
                },
                '&>:first-child': {
                  marginTop: 0,
                },
                '&>:last-child': {
                  marginBottom: 0,
                },
                '.md-inline-image': {
                  my: '1.6rem',
                },
              },
            }}
          >
            {children}
          </ol>
        ),
        img: ({ src, ...props }) => {
          // perform rewrite of image path for images hosted in CMS to use cache
          // instead of trying to hit the CMS directly
          let newSrc = src
          if (process.env.DIRECTUS_API_URL && src?.startsWith(process.env.DIRECTUS_API_URL)) {
            newSrc =
              process.env.GATSBY_WEBSITE_BASE_URL +
              src?.substring(process.env.DIRECTUS_API_URL?.length)
          }

          // this is a hack to support images from the old production CMS that were embedded in Markdown
          // and have the wrong base URL hardcoded in
          if (src?.startsWith('https://du.int.windscribe.com')) {
            newSrc =
              process.env.GATSBY_WEBSITE_BASE_URL +
              src.substring('https://du.int.windscribe.com'.length)
          }
          return (
            <div
              className="md-inline-image"
              sx={{
                backgroundColor: 'white05',
                borderRadius: '1.2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                my: '1.6rem',
              }}
            >
              {/* alt text _should_ be included in the props, but it isn't in the TS type */}
              {/* eslint-disable-next-line jsx-a11y/alt-text */}
              <img sx={{ maxHeight: '40rem', maxWidth: '100%' }} src={newSrc} {...props} />
            </div>
          )
        },
      }}
      {...rest}
    >
      {children}
    </ReactMarkdown>
  )
}
